import { DroplistConfig, DroplistField } from 'src/types/Droplist'
import { Checkbox, Grid, Input, Label, TextArea } from '../affiliate/ui'

/** ISO → the value a datetime-local input wants, in the admin's own time zone. */
const toLocalInput = (iso: string) => {
    const date = new Date(iso)
    if (Number.isNaN(date.getTime())) return ''
    return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16)
}

/** Starting values: what the last run used, else each field's default. */
export const initialConfig = (fields: DroplistField[], previous?: DroplistConfig | null): DroplistConfig => {
    const config: DroplistConfig = {}
    for (const field of fields) {
        const value = previous?.[field.key] ?? field.default
        if (field.type === 'checkbox') config[field.key] = value === null ? false : !!value
        else if (field.type === 'datetime') config[field.key] = typeof value === 'string' ? toLocalInput(value) : ''
        else config[field.key] = typeof value === 'string' ? value : ''
    }
    return config
}

/** What to send: datetime-local values become ISO (they're read as the admin's local time). */
export const configToSend = (fields: DroplistField[], config: DroplistConfig): DroplistConfig => {
    const out: DroplistConfig = {}
    for (const field of fields) {
        const value = config[field.key]
        out[field.key] =
            field.type === 'datetime' && typeof value === 'string' && value ? new Date(value).toISOString() : value
    }
    return out
}

const ConfigFields = ({
    fields,
    value,
    onChange,
}: {
    fields: DroplistField[]
    value: DroplistConfig
    onChange: (value: DroplistConfig) => void
}) => {
    const set = (key: string, next: string | boolean) => onChange({ ...value, [key]: next })
    const label = (field: DroplistField) => `${field.label}${field.required ? '' : ' (optional)'}`

    // In declared order; runs of short fields (text, dates) share a grid row.
    const groups: DroplistField[][] = []
    for (const field of fields) {
        const short = field.type === 'text' || field.type === 'datetime'
        const last = groups[groups.length - 1]
        if (short && last && (last[0].type === 'text' || last[0].type === 'datetime')) last.push(field)
        else groups.push([field])
    }

    const input = (field: DroplistField) => {
        const text = String(value[field.key] ?? '')
        const placeholder = field.placeholder ?? undefined
        if (field.type === 'textarea') {
            return <TextArea value={text} placeholder={placeholder} onChange={(e) => set(field.key, e.target.value)} />
        }
        return (
            <Input
                type={field.type === 'datetime' ? 'datetime-local' : field.type === 'url' ? 'url' : 'text'}
                value={text}
                placeholder={placeholder}
                required={field.required}
                onChange={(e) => set(field.key, e.target.value)}
            />
        )
    }

    return (
        <>
            {groups.map((group) =>
                group[0].type === 'checkbox' ? (
                    <Checkbox key={group[0].key}>
                        <input
                            type="checkbox"
                            checked={!!value[group[0].key]}
                            onChange={(e) => set(group[0].key, e.target.checked)}
                        />
                        {group[0].label}
                    </Checkbox>
                ) : group[0].type === 'url' || group[0].type === 'textarea' ? (
                    <Label key={group[0].key}>
                        {label(group[0])}
                        {input(group[0])}
                    </Label>
                ) : (
                    <Grid key={group[0].key} $min={220}>
                        {group.map((field) => (
                            <Label key={field.key}>
                                {label(field)}
                                {input(field)}
                            </Label>
                        ))}
                    </Grid>
                )
            )}
        </>
    )
}

export default ConfigFields
